import LicenseFeatureGroupCapabilityGroupHeader from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCapabilityGroupHeader"
import LicenseFeatureGroupCapabilityRow from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCapabilityRow"
import { CAPABILITY_FEATURE_TYPE_CODE } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import { useFeatureGroupLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import { isClosedForLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import type { FeatureGroupRuleLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { capabilityGroupStateOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capabilityGroup.utils"
import {
  capabilityTreeOf,
  moduleNameOf,
  packagesOf,
  rulesWithModuleRule,
  type LicenseFeatureGroupCapability,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"

export type LicenseFeatureGroupCapabilityGroupProps = {
  capabilities: readonly LicenseFeatureGroupCapability[]
  /** The package being edited, so "also in" never names it. */
  featureGroupName: string
  /** The rule draft; the module's "everything, now and later" lives here. */
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  /** The draft. */
  isPicked(code: string): boolean
  /** What is persisted — a row whose draft differs is marked as a change. */
  wasPicked(code: string): boolean
  onRulesChange(rules: readonly FeatureGroupRuleLike[]): void
  onSetPicked(
    capabilities: readonly LicenseFeatureGroupCapability[],
    picked: boolean,
  ): void
}

// One module of the app (Property, Map, Filter, …) as a card. The header
// IS the module's own surface — the whole-module grant — with a tri-state
// checkbox over everything in the card. Ticked, the package carries the
// module as a RULE: everything in it now, and every surface a later release
// adds to it, the moment its deploy lands (common.FeatureGroupRules kind 3);
// the rows beneath show ticked and still, "whole module", like content
// under a class rule. Unticked, none of it; anything between — surfaces
// picked one by one — shows as indeterminate with the count, and says that
// new surfaces will not follow. Beneath the header, the surfaces that can
// be granted on their own (the panel's tabs, the map's tools), each with
// what sits inside it behind a chevron of its own. A module nothing can be
// carved out of — Filter, Tags — is the header alone.
//
// This file composes: the arithmetic is capabilityGroupStateOf, the header
// is its own component, the rows are LicenseFeatureGroupCapabilityRow.
function LicenseFeatureGroupCapabilityGroup(
  props: LicenseFeatureGroupCapabilityGroupProps,
) {
  const {
    capabilities,
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    onRulesChange,
    onSetPicked,
  } = props

  const tree = capabilityTreeOf(capabilities)
  const rows = tree.root ? tree.childrenOf(tree.root.Code) : tree.topLevel
  const moduleName = moduleNameOf(capabilities[0])
  const lens = useFeatureGroupLens()
  const state = capabilityGroupStateOf({
    capabilities,
    tree,
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    lens,
    isClosed: isClosedForLens(lens, CAPABILITY_FEATURE_TYPE_CODE),
  })

  function setModuleGranted(granted: boolean) {
    // The module rule covers everything in the card, so picks inside it are
    // redundant either way: granting replaces them, clearing takes
    // everything with it.
    onRulesChange(rulesWithModuleRule(rules, state.moduleCode, granted))
    onSetPicked(capabilities, false)
  }

  function setSameAs(packageName: string) {
    const theirs = capabilities.filter((capability) =>
      packagesOf(capability).includes(packageName),
    )
    // A sibling that carries the whole module is copied as the rule, not
    // as a row per surface; anything less is copied surface by surface.
    if (theirs.length === capabilities.length) {
      setModuleGranted(true)
      return
    }
    setModuleGranted(false)
    onSetPicked(theirs, true)
  }

  return (
    <section className="min-w-0 overflow-hidden rounded-md border">
      <LicenseFeatureGroupCapabilityGroupHeader
        moduleName={moduleName}
        featureGroupName={featureGroupName}
        capabilities={capabilities}
        tree={tree}
        state={state}
        hasRows={rows.length > 0}
        onSetModuleGranted={setModuleGranted}
        onSameAs={setSameAs}
      />

      {rows.length > 0 && (
        <ul className="divide-y">
          {rows.map((capability) => (
            <LicenseFeatureGroupCapabilityRow
              key={capability.Code}
              capability={capability}
              tree={tree}
              featureGroupName={featureGroupName}
              rules={rules}
              persistedRules={persistedRules}
              isPicked={isPicked}
              wasPicked={wasPicked}
              onSetPicked={onSetPicked}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

export default LicenseFeatureGroupCapabilityGroup
